import { useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE } from '../utils/constants';

// Generate or retrieve session ID from localStorage
function getSessionId() {
  const key = 'canton_session_id';
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

// Get common headers for API calls
function getHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-Session-Id': getSessionId(),
  };
}

export function useChat() {
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [error, setError] = useState(null);

  // AbortController for canceling requests
  const abortControllerRef = useRef(null);
  // Track the last user message for regeneration
  const lastUserMessageRef = useRef(null);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load all conversations for this session
  const loadConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const response = await fetch(`${API_BASE}/conversations`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Load a specific conversation
  const loadConversation = useCallback(async (conversationId) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to load conversation');
      }

      const data = await response.json();
      setCurrentConversationId(conversationId);
      setMessages(data.messages.map(m => ({
        role: m.role,
        content: m.content,
        sources: m.sources || [],
      })));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send a message (non-streaming fallback)
  const sendMessage = useCallback(async (content) => {
    if (!content.trim()) return;

    setError(null);

    // Add user message optimistically
    const userMessage = { role: 'user', content };
    setMessages(prev => [...prev, userMessage]);

    // Build history for context (last 10 messages)
    const history = [...messages, userMessage].slice(-10);

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          message: content,
          conversationId: currentConversationId,
          history: history.map(m => ({ role: m.role, content: m.content })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Update conversation ID if new
      if (data.isNewConversation && data.conversationId) {
        setCurrentConversationId(data.conversationId);
        // Reload conversations to show the new one
        loadConversations();
      }

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: data.answer,
        sources: data.sources || [],
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError(err.message);
      // Remove the user message if we failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  }, [messages, currentConversationId, loadConversations]);

  // Send a message with streaming response
  const sendMessageStream = useCallback(async (content, isRegenerate = false) => {
    if (!content.trim()) return;

    setError(null);

    // Store for potential regeneration
    lastUserMessageRef.current = content;

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    // If regenerating, remove the last assistant message
    if (isRegenerate) {
      setMessages(prev => {
        const newMessages = [...prev];
        // Remove last assistant message if present
        if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant') {
          newMessages.pop();
        }
        return newMessages;
      });
    } else {
      // Add user message optimistically
      const userMessage = { role: 'user', content };
      setMessages(prev => [...prev, userMessage]);
    }

    // Build history for context (last 10 messages)
    const history = messages.slice(-10);

    setIsLoading(true);

    // Add placeholder assistant message that will be updated as chunks arrive
    const assistantMessage = {
      role: 'assistant',
      content: '',
      sources: [],
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      const response = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          message: content,
          conversationId: currentConversationId,
          history: history.map(m => ({ role: m.role, content: m.content })),
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'error') {
                throw new Error(data.error);
              }

              if (data.type === 'conversation') {
                if (data.isNewConversation && data.conversationId) {
                  setCurrentConversationId(data.conversationId);
                  loadConversations();
                }
              }

              if (data.type === 'sources') {
                // Update sources on the assistant message
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (updated[lastIdx]?.role === 'assistant') {
                    updated[lastIdx] = { ...updated[lastIdx], sources: data.sources };
                  }
                  return updated;
                });
              }

              if (data.type === 'text') {
                // Append text chunk to assistant message
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (updated[lastIdx]?.role === 'assistant') {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      content: updated[lastIdx].content + data.content,
                    };
                  }
                  return updated;
                });
              }

              if (data.type === 'done') {
                // Mark streaming as complete
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (updated[lastIdx]?.role === 'assistant') {
                    updated[lastIdx] = { ...updated[lastIdx], isStreaming: false };
                  }
                  return updated;
                });
              }
            } catch (parseErr) {
              // Skip malformed JSON lines
              if (parseErr.message !== 'Unexpected end of JSON input') {
                console.warn('SSE parse error:', parseErr);
              }
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User stopped generation - mark message as not streaming but keep content
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.role === 'assistant') {
            updated[lastIdx] = {
              ...updated[lastIdx],
              isStreaming: false,
              wasStopped: true,
            };
          }
          return updated;
        });
      } else {
        setError(err.message);
        // Remove both user and placeholder assistant messages if failed
        setMessages(prev => prev.slice(0, -2));
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [messages, currentConversationId, loadConversations]);

  // Stop the current generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Regenerate the last response
  const regenerateLastResponse = useCallback(() => {
    if (lastUserMessageRef.current && !isLoading) {
      sendMessageStream(lastUserMessageRef.current, true);
    }
  }, [isLoading, sendMessageStream]);

  // Start a new conversation
  const newConversation = useCallback(() => {
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
    lastUserMessageRef.current = null;
  }, []);

  // Delete a conversation
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (response.ok) {
        // Remove from list
        setConversations(prev => prev.filter(c => c.id !== conversationId));

        // If it was the current conversation, clear it
        if (currentConversationId === conversationId) {
          newConversation();
        }
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  }, [currentConversationId, newConversation]);

  // Rename a conversation
  const renameConversation = useCallback(async (conversationId, title) => {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ title }),
      });

      if (response.ok) {
        const updated = await response.json();
        setConversations(prev =>
          prev.map(c => c.id === conversationId ? { ...c, title: updated.title } : c)
        );
      }
    } catch (err) {
      console.error('Failed to rename conversation:', err);
    }
  }, []);

  return {
    // Messages
    messages,
    isLoading,
    error,
    sendMessage,
    sendMessageStream,
    stopGeneration,
    regenerateLastResponse,

    // Conversations
    conversations,
    currentConversationId,
    isLoadingConversations,
    loadConversation,
    loadConversations,
    newConversation,
    deleteConversation,
    renameConversation,
  };
}
